import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Upload, FileText, X } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { useSeo } from "@/hooks/useSeo";
import { submitInternship, MODES, GENDERS, CURRENT_YEARS, isValidIndianMobile, isValidEmail } from "@/lib/api";

// Generic duration-based options so admins can map the applicant to any actual course
// without the form naming specific programs.
const PROGRAM_OPTIONS = [
  "1 Month",
  "3 Months",
  "6 Months",
  "1 Year",
  "Flexible / Not Sure",
];

const Field = ({ children }) => <div className="flex flex-col gap-2">{children}</div>;

const Select = ({ label, id, value, onChange, options, error, placeholder, required, testId }) => (
  <Field>
    <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-navy/70">
      {label} {required && <span className="text-orange">*</span>}
    </label>
    <select id={id} value={value} onChange={onChange} data-testid={testId} className={`w-full rounded-lg border px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-royal ${error ? "border-destructive" : "border-navy/15"}`}>
      <option value="">{placeholder || "Select"}</option>
      {options.map((o) => (<option key={o} value={o}>{o}</option>))}
    </select>
    {error && <span className="text-xs text-destructive">{error}</span>}
  </Field>
);

const initial = {
  fullName: "", mobile: "", whatsapp: "", email: "", dob: "", gender: "", city: "", state: "",
  collegeName: "", universityName: "", course: "", currentYear: "", passingYear: "",
  percentageCgpa: "", technicalSkills: "", programInterested: "", preferredMode: "", whyJoin: "",
};

export default function Apply() {
  useSeo("Apply for Internship", "Apply for ASAI internships and programs. Submit your application with resume — shortlisted candidates will be contacted by our team.");

  const [form, setForm] = useState(initial);
  const [resume, setResume] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return setResume(null);
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "docx"].includes(ext)) {
      setErrors((p) => ({ ...p, resume: "Only PDF and DOCX files are allowed." }));
      setResume(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((p) => ({ ...p, resume: "Resume must be 5 MB or smaller." }));
      setResume(null);
      return;
    }
    setErrors((p) => ({ ...p, resume: undefined }));
    setResume(file);
  };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Full name is required.";
    if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (form.whatsapp && !isValidIndianMobile(form.whatsapp)) e.whatsapp = "Enter a valid WhatsApp number.";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.collegeName.trim()) e.collegeName = "College name is required.";
    if (!form.course.trim()) e.course = "Course is required.";
    if (!form.passingYear.trim()) e.passingYear = "Passing year is required.";
    if (!form.programInterested) e.programInterested = "Please select a program.";
    if (!consent) e.consent = "You must provide consent to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("consent", consent ? "true" : "false");
      if (resume) fd.append("resume", resume);
      const res = await submitInternship(fd);
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div data-testid="page-apply">
        <PageHeader overline="Application received" titleLines={["Thank you", "for applying"]} />
        <section className="bg-white py-24 md:py-32">
          <div className="container-asai max-w-2xl">
            <div data-testid="apply-success" className="flex flex-col items-center gap-6 rounded-3xl border border-navy/10 bg-lightgrey p-10 text-center">
              <CheckCircle2 className="h-16 w-16 text-orange" />
              <p className="text-lg text-navy/75">{result.message}</p>
              <div className="rounded-2xl border border-navy/10 bg-white px-8 py-5">
                <div className="text-xs uppercase tracking-wider text-navy/50">Your application reference number</div>
                <div data-testid="apply-reference" className="mt-1 font-heading text-2xl font-bold text-royal">{result.referenceNumber}</div>
              </div>
              <p className="text-sm text-navy/55">Please save this reference number for future communication.</p>
              <PrimaryButton
                href="https://app.atmasammanschoolofai.com/courses"
                target="_blank"
                rel="noopener noreferrer"
                withArrow
                testId="apply-explore-programs"
              >
                Explore Programs
              </PrimaryButton>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="page-apply">
      <PageHeader
        overline="Apply"
        titleLines={["Apply for an", "ASAI internship"]}
        description="Fill in your details below. Shortlisted candidates will be contacted by our team."
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container-asai max-w-4xl">
          <form onSubmit={onSubmit} noValidate data-testid="apply-form" className="flex flex-col gap-8 rounded-3xl border border-navy/10 bg-lightgrey p-6 sm:p-10">
            {/* Personal */}
            <div>
              <h3 className="mb-5 font-heading text-xl text-navy">Personal Details</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput label="Full Name *" id="ap-name" value={form.fullName} onChange={set("fullName")} error={errors.fullName} placeholder="Your full name" testId="ap-fullName" />
                <FormInput label="Mobile Number *" id="ap-mobile" value={form.mobile} onChange={set("mobile")} error={errors.mobile} placeholder="10-digit mobile" testId="ap-mobile" />
                <FormInput label="WhatsApp Number" id="ap-wa" value={form.whatsapp} onChange={set("whatsapp")} error={errors.whatsapp} placeholder="WhatsApp number" testId="ap-whatsapp" />
                <FormInput label="Email Address *" id="ap-email" type="email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" testId="ap-email" />
                <FormInput label="Date of Birth" id="ap-dob" type="date" value={form.dob} onChange={set("dob")} testId="ap-dob" />
                <Select label="Gender" id="ap-gender" value={form.gender} onChange={set("gender")} options={GENDERS} placeholder="Select gender" testId="ap-gender" />
                <FormInput label="City" id="ap-city" value={form.city} onChange={set("city")} placeholder="City" testId="ap-city" />
                <FormInput label="State" id="ap-state" value={form.state} onChange={set("state")} placeholder="State" testId="ap-state" />
              </div>
            </div>

            {/* Academic */}
            <div>
              <h3 className="mb-5 font-heading text-xl text-navy">Academic Details</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput label="College Name *" id="ap-college" value={form.collegeName} onChange={set("collegeName")} error={errors.collegeName} placeholder="College name" testId="ap-collegeName" />
                <FormInput label="University Name" id="ap-univ" value={form.universityName} onChange={set("universityName")} placeholder="University name" testId="ap-universityName" />
                <FormInput label="Course *" id="ap-course" value={form.course} onChange={set("course")} error={errors.course} placeholder="e.g. B.Tech, BCA" testId="ap-course" />
                <Select label="Current Year" id="ap-cy" value={form.currentYear} onChange={set("currentYear")} options={CURRENT_YEARS} placeholder="Select year" testId="ap-currentYear" />
                <FormInput label="Passing Year *" id="ap-py" value={form.passingYear} onChange={set("passingYear")} error={errors.passingYear} placeholder="e.g. 2025" testId="ap-passingYear" />
                <FormInput label="Percentage / CGPA" id="ap-cgpa" value={form.percentageCgpa} onChange={set("percentageCgpa")} placeholder="e.g. 8.2 / 82%" testId="ap-percentageCgpa" />
              </div>
            </div>

            {/* Program */}
            <div>
              <h3 className="mb-5 font-heading text-xl text-navy">Program & Skills</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select label="Internship Program Interested In" id="ap-prog" value={form.programInterested} onChange={set("programInterested")} options={PROGRAM_OPTIONS} error={errors.programInterested} placeholder="Select a program" required testId="ap-programInterested" />
                <Select label="Preferred Mode" id="ap-mode" value={form.preferredMode} onChange={set("preferredMode")} options={MODES} placeholder="Select mode" testId="ap-preferredMode" />
              </div>
              <div className="mt-4 grid gap-4">
                <FormInput label="Technical Skills" id="ap-skills" value={form.technicalSkills} onChange={set("technicalSkills")} placeholder="e.g. Python, SQL, Machine Learning" testId="ap-technicalSkills" />
                <FormInput label="Why do you want to join?" id="ap-why" as="textarea" value={form.whyJoin} onChange={set("whyJoin")} placeholder="Tell us why you want to join ASAI" testId="ap-whyJoin" />
              </div>
            </div>

            {/* Resume */}
            <div>
              <h3 className="mb-5 font-heading text-xl text-navy">Resume Upload</h3>
              <label htmlFor="ap-resume" className={`flex cursor-pointer items-center gap-4 rounded-xl border border-dashed p-5 transition-colors hover:border-royal ${errors.resume ? "border-destructive" : "border-navy/25"}`}>
                {resume ? <FileText className="h-6 w-6 text-royal" /> : <Upload className="h-6 w-6 text-navy/50" />}
                <div className="flex-1">
                  <div className="text-sm font-medium text-navy">{resume ? resume.name : "Upload your resume (PDF or DOCX, max 5 MB)"}</div>
                  <div className="text-xs text-navy/50">{resume ? `${(resume.size / 1024).toFixed(0)} KB` : "Click to browse"}</div>
                </div>
                {resume && (
                  <button type="button" onClick={(e) => { e.preventDefault(); setResume(null); }} className="text-navy/50 hover:text-destructive" aria-label="Remove file">
                    <X className="h-5 w-5" />
                  </button>
                )}
                <input id="ap-resume" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onFile} className="hidden" data-testid="ap-resume" />
              </label>
              {errors.resume && <span className="mt-2 block text-xs text-destructive">{errors.resume}</span>}
            </div>

            {/* Consent */}
            <div>
              <label className="flex items-start gap-3 text-sm text-navy/75">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="ap-consent" className="mt-1 h-4 w-4 accent-[hsl(21,100%,50%)]" />
                <span>I consent to ASAI storing and using my details to process my application and contact me regarding programs and internships. *</span>
              </label>
              {errors.consent && <span className="mt-2 block text-xs text-destructive">{errors.consent}</span>}
            </div>

            <PrimaryButton type="submit" size="lg" disabled={submitting} testId="apply-submit">
              {submitting ? "Submitting…" : "Submit Application"}
            </PrimaryButton>
          </form>
        </div>
      </section>
    </div>
  );
}
